import type { App } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { setupPageGuard } from './permission'
import { Layout, LayoutAuth, LayoutStudent, LayoutTeacher } from '@/components/layout'
import { useClassroomStore, useUserStore } from '@/store'
import { checkMaintenanceStatus } from '@/api/system'
import { ROUTE_STUDENT_CONFIGS, ROUTE_TEACHER_CONFIGS, convertToVueRoutes } from '@/config/routes'

const userStore = useUserStore()

// Breadcrumb interface
export interface BreadcrumbItem {
  title: string
  name?: string
  path?: string
  params?: Record<string, any>
  icon?: string
}

// Enhanced route meta interface
export interface CustomRouteMeta extends Record<PropertyKey, unknown> {
  title?: string
  group?: string
  requiresAuth?: boolean
  /** Only allow ADMIN to access this route */
  requiresAdmin?: boolean
  icon?: string
  i18nKey?: string
  breadcrumb?: {
    parent?: string[]
    title?: string | ((route: any) => string)
    params?: string[]
    icon?: string
    i18nKey?: string
  }
}

// Get dynamic routes from central config
const dynamicRoutes = convertToVueRoutes()

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Root',
    component: Layout,
    redirect: '/agent-management',
    children: dynamicRoutes,
  },

  // CLASSROOM
  {
    path: '/classroom',
    name: 'classroom-v2',
    component: () => import('@/views/classroom-v2/index.vue'),
    meta: {
      title: 'classroom',
    },
    beforeEnter: async (_to, _from, next) => {
      const role = userStore.role
      if (role?.code === 'ADMIN') {
        const classroomStore = useClassroomStore()

        try {
          await classroomStore.fetchClassrooms(userStore.workSpaceId as string | number)
          const classrooms = classroomStore.getClassrooms

          if (classrooms?.length) {
            next({
              name: 'classroom.teacher.board',
              params: { classroomId: classrooms[0].id },
            })
            return
          }

          next({ name: 'classroom.teacher.list' })
        }
        catch (_error) {
          next({ name: 'classroom.teacher.list' })
        }

        return
      }

      next()
    },
  },
  {
    path: '/lesson-workspace/:lessonId',
    name: 'lesson-workspace',
    component: () => import('@/views/classroom-v2/components/Teacher/LessonWorkspace/index.vue'),
    meta: {
      title: 'Lesson Workspace',
    },
  },
  {
    path: '/',
    name: 'teacherRoot',
    component: LayoutTeacher,
    redirect: { name: 'classroom.teacher.detail' },
    children: ROUTE_TEACHER_CONFIGS,
  },
  {
    path: '/',
    name: 'studentRoot',
    component: LayoutStudent,
    redirect: { name: 'classroom.student.detail' },
    children: ROUTE_STUDENT_CONFIGS,
  },

  // AUTH ROUTES
  // {
  //   path: '/login',
  //   name: 'login',
  //   component: LayoutAuth,
  //   redirect: '/login',
  //   children: [
  //     {
  //       path: '/login',
  //       name: 'loginPage',
  //       component: () => import('@/views/user/login.vue'),
  //       meta: {
  //         title: '',
  //       },
  //     },
  //
  //   ],
  // },
  {
    path: '/login-student',
    name: 'login-student',
    component: () => import('@/views/user/loginFormLanding/student/index.vue'),
    meta: {
      title: '',
    },
  },
  {
    path: '/login-parent',
    name: 'login-parent',
    component: () => import('@/views/user/loginFormLanding/parent/index.vue'),
    meta: {
      title: '',
    },
  },
  {
    path: '/login',
    name: 'login-teacher',
    component: () => import('@/views/user/loginFormLanding/teacher/index.vue'),
    meta: {
      title: '',
    },
  },
  {
    path: '/signup',
    name: 'sign-up',
    component: () => import('@/views/user/SignUpForm/SignUp.vue'),
    meta: {
      title: '',
    },
  },
  {
    path: '/signup-student',
    name: 'sign-up-student',
    component: () => import('@/views/user/SignUpForm/SignUpStudent.vue'),
    meta: {
      title: '',
    },
  },

  {
    path: '/sign-up',
    name: 'SignUp',
    component: LayoutAuth,
    redirect: '/sign-up',
    children: [
      {
        path: '',
        name: 'SignUpPage',
        component: () => import('@/views/user/SignUp.vue'),
        meta: {
          title: 'Sign Up',
        },
      },
    ],
  },
  // {
  //   path: '/forgot-password',
  //   name: 'forgot-password',
  //   component: LayoutAuth,
  //   redirect: '/forgot-password',
  //   children: [
  //     {
  //       path: '',
  //       name: 'ForgotPasswordPage',
  //       component: () => import('@/views/user/ForgotPassword.vue'),
  //       meta: {
  //         title: 'Forgot Password',
  //       },
  //     },
  //   ],
  // },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/user/components/ForgotPassword/ForgotPassword.vue'),
  },
  {
    path: '/forgot-password-student',
    name: 'forgot-password-student',
    component: () => import('@/views/user/components/ForgotPassword/ForgotPasswordStudent.vue'),
  },

  // OTHER ROUTES
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/maintenance/index.vue'),
    meta: {
      title: 'maintenance',
    },
  },
  {
    path: '/share/:appCode',
    name: 'share-chat',
    component: () => import('@/views/share-chat/index.vue'),
  },
  {
    path: '/embed/:appCode',
    name: 'embed-chat',
    component: () => import('@/views/embed-chat/index.vue'),
  },
  {
    path: '/workspace',
    name: 'Workspace',
    component: () => import('@/views/user/WorkSpace.vue'),
  },
  {
    path: '/space',
    name: 'space',
    component: () => import('@/views/user/Space.vue'),
  },
  {
    path: '/share-workspace/pending',
    name: 'share-workspace.pending',
    component: () => import('@/views/user/components/TeacherSharing.vue'),
  },

  {
    path: '/space-student',
    name: 'space-student',
    component: () => import('@/views/user/SpaceStudent.vue'),
  },
  // Join Class Waiting screen
  {
    path: '/join-class-waiting',
    name: 'join-class-waiting',
    component: () => import('@/views/user/components/JoinClassWaiting.vue'),
    meta: {
      title: 'Join Class Waiting',
    },
  },

  // ERROR ROUTES
  {
    path: '/404',
    name: '404',
    component: () => import('@/components/exception/404/index.vue'),
  },
  {
    path: '/500',
    name: '500',
    component: () => import('@/components/exception/500/index.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    redirect: '/404',
  },
]

console.log('base url:', import.meta.env.VITE_BASE_URL)
export const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL || '/'),
  routes,
  scrollBehavior: () => ({ left: 0, top: 0 }),
})

// Thời gian cache trạng thái bảo trì (5 giây)
const MAINTENANCE_CACHE_TIME = 5000

// Lưu trạng thái bảo trì trong memory
let maintenanceStatus = {
  maintenance: false,
  role: '',
  estimated_time: 0,
  lastChecked: 0,
}

// Biến để theo dõi xem có cần kiểm tra ngay lập tức không
let shouldCheckImmediately = false

// Hàm kiểm tra trạng thái bảo trì
async function checkMaintenance(forceCheck = false) {
  const currentTime = Date.now()

  // Kiểm tra ngay lập tức nếu được yêu cầu hoặc cache đã hết hạn
  if (forceCheck || shouldCheckImmediately || currentTime - maintenanceStatus.lastChecked > MAINTENANCE_CACHE_TIME) {
    try {
      const { data } = await checkMaintenanceStatus()
      maintenanceStatus = {
        maintenance: data?.maintenance || false,
        role: data?.role || '',
        estimated_time: data?.estimated_time || 0,
        lastChecked: currentTime,
      }
      localStorage.setItem('estimated_time', JSON.stringify(maintenanceStatus.estimated_time))

      // Reset flag sau khi đã kiểm tra
      shouldCheckImmediately = false
    }
    catch (error) {
      console.error('Error checking maintenance status:', error)
    }
  }

  return maintenanceStatus
}

// router.beforeEach(async (to, from, next) => {
//   // Nếu đang ở trang login hoặc maintenance, bỏ qua kiểm tra
//   if (to.name === 'login' || to.name === 'maintenance') {
//     next()
//     return
//   }
//
//   const authStore = useAuthStore()
//   const token = authStore.token
//
//   // Kiểm tra xem route có yêu cầu xác thực không
//   if (to.meta.requiresAuth && !token) {
//     next({ name: 'login' })
//     return
//   }
//
//   // Nếu đã đăng nhập và cố truy cập trang login, chuyển hướng về trang chủ
//   if (token && (to.name === 'login' || to.name === 'loginPage')) {
//     next({ name: 'dashboard' })
//     return
//   }
//
//   // Kiểm tra trạng thái bảo trì chỉ khi đã đăng nhập
//   if (token) {
//     try {
//       // Kiểm tra xem có phải là lần đầu login hay không
//       const isFirstLoginCheck = from.name === 'login' || from.name === 'loginPage'
//
//       // Nếu là lần đầu login, buộc phải kiểm tra maintenance ngay lập tức
//       const status = await checkMaintenance(isFirstLoginCheck)
//       const { maintenance, role } = status
//
//       // Nếu đang bảo trì và không phải môi trường dev hoặc super_admin
//       if (maintenance && role !== 'dev' && role !== 'super_admin' && to.name !== 'maintenance') {
//         next({ name: 'maintenance' })
//         return
//       }
//
//       // Nếu không bảo trì và đang ở trang maintenance, chuyển về trang chủ
//       if (!maintenance && to.name === 'maintenance') {
//         next({ name: 'dashboard' })
//         return
//       }
//     }
//     catch (error) {
//       console.error('Error checking maintenance status:', error)
//     }
//   }
//
//   next()
// })

setupPageGuard(router)

export async function setupRouter(app: App) {
  app.use(router)
  await router.isReady()
}
